import { type Theme } from '@electron/shared';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import {
  makePersistable,
  pausePersisting,
  startPersisting,
  stopPersisting,
} from 'mobx-persist-store';
import { type ReactNode } from 'react';

import {
  SHELL_THEMES,
  type ShellTheme,
} from '@/features/shell-theme/shell-theme.constants';
import { DeviceSettingsStorage } from '@/utils/localStorage';

export enum ChatWidth {
  Narrow = 'max-w-[880px]',
  Wide = 'max-w-screen-xl',
}

export type SidebarSection = 'routines' | 'projects' | 'favorites' | 'recents';
export type MainSidebarNavItem =
  | 'feed'
  | 'meetings'
  | 'agents'
  | 'journal'
  | 'integrations'
  | 'library'
  | 'wiki'
  | 'extras';

export const COMMAND_BAR_SCOPES = [
  'All',
  'Chats',
  'Messages',
  'Meetings',
  'Routines',
  'Journals',
] as const;
export type CommandBarScope = (typeof COMMAND_BAR_SCOPES)[number];

export const COMMAND_BAR_STATE_TTL_MS = 5 * 60_000;

export const SIDEBAR_MIN_WIDTH = 200;

/** Dedupes double-fires of the sidebar toggle. */
const SIDEBAR_TOGGLE_COOLDOWN_MS = 100;

export enum Panel {
  Chats = '/chats',
  Journal = '/journal',
  Meetings = '/meetings',
  Routines = '/routines',
  Home = '/',
  WorldModelDoc = '/world-model',
  Echonotes = '/echonotes',
  Debug = '/debug',
  Ingestion = '/ingestion',
  Arena = '/arena',
  Subscription = '/subscription',
  CkDebug = '/ck-debug',
  MeetingDebug = '/meeting-debug',
  McpInfo = '/mcp-info',
  SummaryTimeline = '/summary-timeline',
  ActivityTimeline = '/activity-timeline',
  ChannelSimulator = '/channel-simulator',
}

const THEME_COOKIE = 'littlebird_theme';

export type ThemePreference = Theme | 'system';
export { type Theme };

export type WindowTitle = {
  title?: string | ReactNode;
  altTitle: string;
};

export const THEME_MEDIA_QUERY = window.matchMedia(
  '(prefers-color-scheme: dark)'
);

export const getSystemTheme = (): Theme =>
  THEME_MEDIA_QUERY.matches ? 'dark' : 'light';

const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

function getInitialThemePreference(): ThemePreference {
  let value = DeviceSettingsStorage.getItem(THEME_COOKIE);
  // The v0.73.21 migration to ds: keys copied some values JSON-quoted, as '"dark"'
  if (value?.startsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (typeof parsed === 'string') value = parsed;
    } catch {
      // Not JSON: validated as is below
    }
  }
  return isThemePreference(value) ? value : 'system';
}

function readStoredAppearance(value: string | null): {
  shellTheme?: ShellTheme;
  shellAccentButtons?: boolean;
} {
  if (!value) return {};

  let stored: unknown;
  try {
    stored = JSON.parse(value);
  } catch {
    return {};
  }

  if (!stored || typeof stored !== 'object') return {};
  const { shellTheme, shellAccentButtons } = stored as Record<string, unknown>;
  return {
    shellTheme: SHELL_THEMES.find((theme) => theme === shellTheme),
    shellAccentButtons:
      typeof shellAccentButtons === 'boolean' ? shellAccentButtons : undefined,
  };
}

export interface PaywallBannerState {
  show: boolean;
  title?: string;
  subtitle?: string;
}

const DEFAULT_PAYWALL_CONTENT = {
  title: 'Ready for the Next Level?',
  subtitle: 'This feature requires a subscription. Upgrade to continue',
} as const;

const usageWarningKey = (resetsAt: string, threshold: number) =>
  `${resetsAt}:${threshold}`;

export class UIStore {
  themePreference: ThemePreference = getInitialThemePreference();
  systemTheme: Theme = getSystemTheme();
  forcedTheme: Theme | null = null;
  currentPanel: Panel | null = Panel.Chats;
  isDraggingFile = false;
  chromelessSurfaces = 0;
  windowTitle: WindowTitle = { altTitle: 'New Chat' };
  pastDueBannerDismissed = false;
  referralCardDismissed = false;
  connectAppsCardDone = false;
  aiWrappedCardDismissed = false;
  meetingProjectTipDismissed = false;
  automaticMeetingPrepBannerDismissed = false;
  trialEndingBannerDismissedOn: string | null = null;
  hasSeenArchivedChatsHint = false;
  dismissedDiscoverableTeamKeys: string[] = [];
  /**
   * Integration cards the user explicitly collapsed on the settings page.
   * Only collapses are tracked, so cards — including newly connected
   * integrations — default to open.
   */
  collapsedIntegrationKeys: string[] = [];
  dismissedUsageWarnings: string[] = [];

  paywallBanner: PaywallBannerState = {
    show: false,
    ...DEFAULT_PAYWALL_CONTENT,
  };

  sidebarCollapsed = false;
  navRailLabelsVisible = false;
  mainSidebarNavItems: MainSidebarNavItem[] = [
    'feed',
    'meetings',
    'journal',
    'integrations',
    'library',
    'wiki',
  ];
  mainSidebarAgentsShown = true;
  mainSidebarExtrasShown = true;
  sidebarExpandedSections: Record<SidebarSection, boolean> = {
    routines: true,
    projects: true,
    favorites: true,
    recents: true,
  };
  sidebarExpandedProjectIds: string[] = [];
  /** Last-known sidebar width tracked from ResizableBox. */
  sidebarWidth = 240;
  /** Tracks whether the viewport is below Tailwind `md` (768px). */
  isNarrowViewport = false;
  private lastSidebarToggleAt = 0;

  commandBarQuery = '';
  commandBarScope: CommandBarScope = 'All';
  private commandBarResetTimer: ReturnType<typeof setTimeout> | null = null;
  private stopSideEffects: (() => void) | null = null;

  arenaSelectedModes: string[] = [];

  chatWidth: ChatWidth = ChatWidth.Narrow;
  shellTheme: ShellTheme = 'default';
  shellAccentButtons = false;

  constructor() {
    makeObservable(this, {
      themePreference: observable,
      systemTheme: observable,
      forcedTheme: observable,
      setForcedTheme: action,
      currentPanel: observable,
      isDraggingFile: observable,
      chromelessSurfaces: observable,
      chromeless: computed,
      enterChromeless: action,
      leaveChromeless: action,
      windowTitle: observable.ref,
      pastDueBannerDismissed: observable,
      referralCardDismissed: observable,
      connectAppsCardDone: observable,
      aiWrappedCardDismissed: observable,
      meetingProjectTipDismissed: observable,
      automaticMeetingPrepBannerDismissed: observable,
      trialEndingBannerDismissedOn: observable,
      dismissTrialEndingBanner: action,
      hasSeenArchivedChatsHint: observable,
      dismissedDiscoverableTeamKeys: observable,
      collapsedIntegrationKeys: observable,
      setIntegrationCollapsed: action,
      setCollapsedIntegrationKeys: action,
      dismissedUsageWarnings: observable,
      dismissUsageWarning: action,
      theme: computed,
      setThemePreference: action,
      setSystemTheme: action,
      setCurrentPanel: action,
      setIsDraggingFile: action,
      setWindowTitle: action,
      dismissPastDueBanner: action,
      dismissReferralCard: action,
      dismissAiWrappedCard: action,
      dismissMeetingProjectTip: action,
      dismissAutomaticMeetingPrepBanner: action,
      setPastDueBannerDismissed: action,
      setReferralCardDismissed: action,
      setConnectAppsCardDone: action,
      setAiWrappedCardDismissed: action,
      setHasSeenArchivedChatsHint: action,
      dismissDiscoverableTeam: action,
      resetDismissedDiscoverableTeams: action,
      paywallBanner: observable,
      showPaywallBanner: action,
      hidePaywallBanner: action,
      sidebarCollapsed: observable,
      navRailLabelsVisible: observable,
      setNavRailLabelsVisible: action,
      mainSidebarNavItems: observable,
      mainSidebarAgentsShown: observable,
      mainSidebarExtrasShown: observable,
      toggleMainSidebarNavItem: action,
      sidebarExpandedSections: observable,
      sidebarExpandedProjectIds: observable,
      sidebarWidth: observable,
      isNarrowViewport: observable,
      isSidebarHidden: computed,
      setSidebarCollapsed: action,
      toggleSidebarCollapsed: action,
      setSidebarSectionExpanded: action,
      setSidebarProjectExpanded: action,
      setSidebarWidth: action,
      setIsNarrowViewport: action,
      commandBarQuery: observable,
      setCommandBarQuery: action,
      commandBarScope: observable,
      setCommandBarScope: action,
      cycleCommandBarScope: action,
      scheduleCommandBarStateReset: action,
      cancelCommandBarStateReset: action,
      arenaSelectedModes: observable,
      setArenaSelectedModes: action,
      chatWidth: observable,
      shellTheme: observable,
      setShellTheme: action,
      shellAccentButtons: observable,
      setShellAccentButtons: action,
      setChatWidth: action,
      toggleChatFullWidth: action,
    });
  }

  /**
   * Reads the saved UI state, then saves every change and follows the system
   * theme and other windows' appearance. Idempotent; returns a disposer
   */
  start(): () => void {
    if (this.stopSideEffects) return this.stopSideEffects;
    this.setThemePreference(getInitialThemePreference());

    // Resuming persistence after a storage event must not echo it to other windows
    void makePersistable(
      this,
      {
        name: 'uiStore',
        properties: [
          'referralCardDismissed',
          'connectAppsCardDone',
          'aiWrappedCardDismissed',
          'meetingProjectTipDismissed',
          'automaticMeetingPrepBannerDismissed',
          'trialEndingBannerDismissedOn',
          'hasSeenArchivedChatsHint',
          'dismissedDiscoverableTeamKeys',
          'collapsedIntegrationKeys',
          'dismissedUsageWarnings',
          'sidebarCollapsed',
          'navRailLabelsVisible',
          'mainSidebarNavItems',
          'mainSidebarAgentsShown',
          'mainSidebarExtrasShown',
          'sidebarExpandedSections',
          'sidebarExpandedProjectIds',
          'chatWidth',
          'shellTheme',
          'shellAccentButtons',
        ],
        storage: window.localStorage,
      },
      { fireImmediately: false }
    );

    const stopCrossWindowThemeSync = this.initCrossWindowThemeSync();

    const followSystemTheme = () => this.setSystemTheme(getSystemTheme());
    THEME_MEDIA_QUERY.addEventListener('change', followSystemTheme);

    const stopThemeCookie = reaction(
      () => this.themePreference,
      (pref) => DeviceSettingsStorage.setItem(THEME_COOKIE, pref),
      { fireImmediately: true }
    );

    const stop = () => {
      if (this.stopSideEffects !== stop) return;
      this.stopSideEffects = null;
      stopThemeCookie();
      THEME_MEDIA_QUERY.removeEventListener('change', followSystemTheme);
      stopCrossWindowThemeSync();
      stopPersisting(this);
      this.cancelCommandBarStateReset();
    };
    this.stopSideEffects = stop;
    return stop;
  }

  private initCrossWindowThemeSync(): () => void {
    const onStorage = ({ key, newValue }: StorageEvent) => {
      if (key === `ds:${THEME_COOKIE}`) {
        if (isThemePreference(newValue)) this.setThemePreference(newValue);
        return;
      }

      if (key !== 'uiStore') return;
      const next = readStoredAppearance(newValue);
      const shellTheme =
        next.shellTheme === this.shellTheme ? undefined : next.shellTheme;
      const shellAccentButtons =
        next.shellAccentButtons === this.shellAccentButtons
          ? undefined
          : next.shellAccentButtons;
      if (shellTheme === undefined && shellAccentButtons === undefined) return;

      pausePersisting(this);
      try {
        if (shellTheme !== undefined) this.setShellTheme(shellTheme);
        if (shellAccentButtons !== undefined) {
          this.setShellAccentButtons(shellAccentButtons);
        }
      } finally {
        startPersisting(this);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }

  get theme(): Theme {
    if (this.forcedTheme) return this.forcedTheme;
    return this.themePreference === 'system'
      ? this.systemTheme
      : this.themePreference;
  }

  setThemePreference(value: ThemePreference) {
    this.themePreference = value;
  }
  setSystemTheme(value: Theme) {
    this.systemTheme = value;
  }
  setForcedTheme(value: Theme | null) {
    this.forcedTheme = value;
  }
  setCurrentPanel(value: Panel | null) {
    this.currentPanel = value;
  }
  get chromeless(): boolean {
    return this.chromelessSurfaces > 0;
  }

  enterChromeless() {
    this.chromelessSurfaces += 1;
  }

  leaveChromeless() {
    this.chromelessSurfaces -= 1;
  }

  setIsDraggingFile(value: boolean) {
    this.isDraggingFile = value;
  }
  setWindowTitle(value: WindowTitle) {
    this.windowTitle = value;
  }
  dismissPastDueBanner() {
    this.pastDueBannerDismissed = true;
  }
  dismissReferralCard() {
    this.referralCardDismissed = true;
  }
  dismissAiWrappedCard() {
    this.aiWrappedCardDismissed = true;
  }
  dismissMeetingProjectTip() {
    this.meetingProjectTipDismissed = true;
  }
  dismissAutomaticMeetingPrepBanner() {
    this.automaticMeetingPrepBannerDismissed = true;
  }
  dismissTrialEndingBanner() {
    this.trialEndingBannerDismissedOn = new Date().toDateString();
  }
  dismissDiscoverableTeam(key: string) {
    if (this.dismissedDiscoverableTeamKeys.includes(key)) return;
    this.dismissedDiscoverableTeamKeys = [
      ...this.dismissedDiscoverableTeamKeys,
      key,
    ];
  }
  resetDismissedDiscoverableTeams() {
    this.dismissedDiscoverableTeamKeys = [];
  }
  setIntegrationCollapsed(key: string, collapsed: boolean) {
    if (collapsed === this.collapsedIntegrationKeys.includes(key)) return;
    this.collapsedIntegrationKeys = collapsed
      ? [...this.collapsedIntegrationKeys, key]
      : this.collapsedIntegrationKeys.filter((k) => k !== key);
  }
  /** Wholesale replacement for collapse-all / expand-all. */
  setCollapsedIntegrationKeys(keys: string[]) {
    this.collapsedIntegrationKeys = keys;
  }
  isUsageWarningDismissed(resetsAt: string, threshold: number): boolean {
    return this.dismissedUsageWarnings.includes(
      usageWarningKey(resetsAt, threshold)
    );
  }
  dismissUsageWarning(resetsAt: string, threshold: number) {
    this.dismissedUsageWarnings = [
      ...this.dismissedUsageWarnings.filter((key) =>
        key.startsWith(`${resetsAt}:`)
      ),
      usageWarningKey(resetsAt, threshold),
    ];
  }
  setPastDueBannerDismissed(value: boolean) {
    this.pastDueBannerDismissed = value;
  }
  setReferralCardDismissed(value: boolean) {
    this.referralCardDismissed = value;
  }
  setConnectAppsCardDone(value: boolean) {
    this.connectAppsCardDone = value;
  }
  setAiWrappedCardDismissed(value: boolean) {
    this.aiWrappedCardDismissed = value;
  }
  setHasSeenArchivedChatsHint(value: boolean) {
    this.hasSeenArchivedChatsHint = value;
  }
  showPaywallBanner(options?: { title?: string; subtitle?: string }) {
    this.paywallBanner = {
      show: true,
      title: options?.title || DEFAULT_PAYWALL_CONTENT.title,
      subtitle: options?.subtitle || DEFAULT_PAYWALL_CONTENT.subtitle,
    };
  }

  hidePaywallBanner() {
    this.paywallBanner = { show: false, ...DEFAULT_PAYWALL_CONTENT };
  }

  setSidebarCollapsed(collapsed: boolean) {
    this.sidebarCollapsed = collapsed;
  }

  toggleSidebarCollapsed() {
    // leading edge: the first toggle acts immediately, repeats inside the
    // cooldown are dropped
    const now = Date.now();
    if (now - this.lastSidebarToggleAt < SIDEBAR_TOGGLE_COOLDOWN_MS) return;
    this.lastSidebarToggleAt = now;
    this.setSidebarCollapsed(!this.sidebarCollapsed);
  }

  toggleMainSidebarNavItem(item: MainSidebarNavItem) {
    if (item === 'agents') {
      this.mainSidebarAgentsShown = !this.mainSidebarAgentsShown;
      return;
    }
    if (item === 'extras') {
      this.mainSidebarExtrasShown = !this.mainSidebarExtrasShown;
      return;
    }
    this.mainSidebarNavItems = this.mainSidebarNavItems.includes(item)
      ? this.mainSidebarNavItems.filter((shown) => shown !== item)
      : [...this.mainSidebarNavItems, item];
  }

  setSidebarWidth(width: number) {
    this.sidebarWidth = width;
  }

  setIsNarrowViewport(value: boolean) {
    this.isNarrowViewport = value;
  }

  /** True when the inline sidebar isn't visible (collapsed or narrow). */
  get isSidebarHidden(): boolean {
    return this.sidebarCollapsed || this.isNarrowViewport;
  }

  setNavRailLabelsVisible(show: boolean) {
    this.navRailLabelsVisible = show;
  }

  setSidebarSectionExpanded(section: SidebarSection, expanded: boolean) {
    this.sidebarExpandedSections = {
      ...this.sidebarExpandedSections,
      [section]: expanded,
    };
  }

  setSidebarProjectExpanded(projectId: string, expanded: boolean) {
    if (expanded === this.sidebarExpandedProjectIds.includes(projectId)) return;
    this.sidebarExpandedProjectIds = expanded
      ? [...this.sidebarExpandedProjectIds, projectId]
      : this.sidebarExpandedProjectIds.filter((id) => id !== projectId);
  }

  setCommandBarQuery(query: string) {
    this.commandBarQuery = query;
  }

  setCommandBarScope(scope: CommandBarScope) {
    this.commandBarScope = scope;
  }

  cycleCommandBarScope(direction: 1 | -1) {
    const count = COMMAND_BAR_SCOPES.length;
    const index = COMMAND_BAR_SCOPES.indexOf(this.commandBarScope);
    this.commandBarScope =
      COMMAND_BAR_SCOPES[(index + direction + count) % count];
  }

  scheduleCommandBarStateReset() {
    this.cancelCommandBarStateReset();
    this.commandBarResetTimer = setTimeout(
      action(() => {
        this.commandBarResetTimer = null;
        this.commandBarQuery = '';
        this.commandBarScope = 'All';
      }),
      COMMAND_BAR_STATE_TTL_MS
    );
  }

  cancelCommandBarStateReset() {
    if (this.commandBarResetTimer === null) return;
    clearTimeout(this.commandBarResetTimer);
    this.commandBarResetTimer = null;
  }

  setArenaSelectedModes(modes: string[]) {
    this.arenaSelectedModes = modes;
  }

  setChatWidth(width: ChatWidth) {
    this.chatWidth = width;
  }

  setShellTheme(theme: ShellTheme) {
    this.shellTheme = theme;
  }

  setShellAccentButtons(enabled: boolean) {
    this.shellAccentButtons = enabled;
  }

  toggleChatFullWidth() {
    this.chatWidth =
      this.chatWidth === ChatWidth.Narrow ? ChatWidth.Wide : ChatWidth.Narrow;
  }
}

export const uiStore = new UIStore();
// Every window starts persistence when this module loads, as before start()
// existed; a test stops it with the disposer and starts its own instance
uiStore.start();
